import "./login.css";
import React, { useEffect, useState } from 'react';
import translations from '../translations';
import LanguageDropDown from '../languageDropDown/dropDown';
import { ChakraProvider, useToast, Spinner } from '@chakra-ui/react';
// import { useNavigate } from "react-router-dom";



const LoginPageContent = () => {
    const toast = useToast({ position: 'top-right' });

    // Expose toast to window for use in handleSubmitRegistration
    useEffect(() => {
        window.showLoginToast = (message, status) => {
            toast({
                title: status === 'success' ? 'Success' : 'Error',
                description: message,
                status: status,
                duration: 3000,
                isClosable: true,
            });
        };
        return () => {
            delete window.showLoginToast;
        };
    }, [toast]);

    // const navigate = useNavigate();
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [browserLanguage, setBrowserLanguage] = useState('en');
    const [modalLanguage, setModalLanguage] = useState('Japanese');
    const [isSubmitting, setIsSubmitting] = useState(false);


    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        // BID redirects the OAuth2 callback here as /login?code=...&state=...
        // Always hand the code to oauth2-proxy to redeem it. Do NOT set
        // 'authCompleted' here: marking auth complete before the session actually
        // exists poisons sessionStorage if the callback fails, which traps the user
        // in an infinite /login ⇄ /tic-bot loop (the code branch never re-runs
        // because the flag is already true).
        if (code && state) {
            window.location.href = `/oauth2/callback?code=${code}&state=${state}`;
            return;
        }

        // No code in the URL → ask oauth2-proxy whether a real session exists.
        // The session cookie is the single source of truth: only enter the app if
        // /oauth2/userinfo confirms a valid session, otherwise stay on the login page.
        fetch('/oauth2/userinfo', { credentials: 'include' })
            .then((res) => {
                if (res.ok) {
                    sessionStorage.setItem('authCompleted', 'true');
                    window.location.href = '/tic-bot';
                } else {
                    sessionStorage.removeItem('authCompleted');
                }
            })
            .catch(() => sessionStorage.removeItem('authCompleted'));

        // Detect browser language
        const lang = navigator.language || navigator.userLanguage;
        if (lang.startsWith('ja')) {
            setBrowserLanguage('ja');
        } else {
            setBrowserLanguage('en');
        }
    }, []);

    const handleLogin = () => {
        sessionStorage.clear();
        // rd=/login so the post-callback landing returns to this page, which then
        // verifies the session via /oauth2/userinfo before entering /tic-bot. Without
        // an explicit rd, nginx's X-Auth-Request-Redirect header ($request_uri) makes
        // oauth2-proxy redirect back to /oauth2/start after the callback, kicking off
        // a SECOND authorization round trip — the user sees the BID account-selection
        // screen twice.
        window.location.href = "/oauth2/start?rd=/login";
    };

    const handleRegister = () => {
        // Sync with sessionStorage when opening modal
        const savedLang = sessionStorage.getItem('langOpt') || 'Japanese';
        setModalLanguage(savedLang);
        setShowRegisterModal(true);
    };

    const handleCloseModal = () => {
        setShowRegisterModal(false);
    };

    const handleSubmitRegistration = async (e) => {
        e.preventDefault();

        const formData = {
            name: e.target.name.value,
            email: e.target.email.value,
            user_id: e.target.userId.value,
            company_name: e.target.companyName.value,
            branch: e.target.branch.value,
            department: e.target.department.value
        };

        setIsSubmitting(true);
        try {
            const response = await fetch('/api/request-approval', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (response.ok && result.status) {
                // Toast will be shown by the wrapper component
                window.showLoginToast?.(
                    modalLanguage === 'Japanese' ? '申請が送信されました' : 'Application submitted successfully',
                    'success'
                );
                setShowRegisterModal(false);
            } else {
                // Close modal first so toast is not hidden behind the overlay
                setShowRegisterModal(false);
                // Always show error in Japanese
                let errorMsg;
                if (response.status === 429) {
                    errorMsg = 'リクエストが多すぎます。しばらくしてからもう一度お試しください。';
                } else {
                    errorMsg = '送信に失敗しました: ' + (result.error || '不明なエラー');
                }
                window.showLoginToast?.(errorMsg, 'error');
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            // Close modal first so toast is not hidden behind the overlay
            setShowRegisterModal(false);
            window.showLoginToast?.('エラーが発生しました', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const translate = (text) => {
        if (!text || !translations[text]) return text;

        switch (modalLanguage) {
            case 'Japanese':
                return translations[text].translationJp || text;
            case 'Indonesian':
                return translations[text].translationIn || text;
            case 'English':
            default:
                return translations[text].translationEng || text;
        }
    };
    return (
        <div className="login">
            <div className="login-top">
                <img className="logo" src="./login-fuso.svg" alt="fuso-logo"></img>
            </div>
            <div className="login-mid">
                <div className="left-side">
                    <img src="./login-bot.svg" alt="bot-image"></img>
                    <div className="portal-name">
                        <p>Welcome to <span>TIC Chatbot Portal</span></p>
                    </div>
                </div>
                <div className="auth-buttons-container">
                    <div onClick={handleLogin} className="login-button">
                        <img src="./microsoft.svg" alt="outlook-logo"></img>
                        <span>Login to Chatbot</span>
                    </div>
                    <div onClick={handleRegister} className="login-button">
                        <img src="./memo.png" alt="register-icon"></img>
                        <span>{browserLanguage === 'ja' ? '新規登録' : 'Register'}</span>
                    </div>
                </div>
            </div>
            <div className="login-bottom" >
                <img src="./back-image.svg" className="background-image" alt="base-image" ></img>
                <div className="features">
                    <div className="feature-card">
                        <img src="./login-avail.svg" alt="feature-availability"></img>
                        <h3>{translate("24/7 Availability")}</h3>
                        <span>{translate("TIC Chatbots provide round-the-clock support.")}</span>
                    </div>
                    <div className="feature-card">
                        <img src="./login-response.svg" alt="feature-availability"></img>
                        <h3>{translate("Instant Response")}</h3>
                        <span>{translate("Deliver immediate replies to user inquiries.")}</span>
                    </div>
                    <div className="feature-card">
                        <img src="./login-consistency.svg" alt="feature-availability"></img>
                        <h3>{translate("Consistency")}</h3>
                        <span>{translate("Provide consistent responses & follow scripts.")}</span>
                    </div>
                    <div className="feature-card">
                        <img src="./login-scalability.svg" alt="feature-availability"></img>
                        <h3>{translate("Scalability")}</h3>
                        <span>{translate("Handle numerous interactions simultaneously.")}</span>
                    </div>
                </div>
                <div className="disclaimer-body">
                    <div className="disclaimer-box">
                        【注意事項】
                        <ul>
                            <li>TIC Chatbotは生成AIを使用した車両修理に関する技術支援システムです</li>
                            <li>多くの利用を経験することで生成AIは学習し、より精度の高い回答を出せるよう成長する</li>
                            <li>回答は全てが十分ではないため、精度の高い回答を得られない場合がある</li>
                        </ul>
                    </div><div className="disclaimer-box">
                        【依頼事項】
                        <ul>
                            <li>回答には社外秘の情報を含むため、そのままの回答を外部に持ち出さない</li>
                            <li>具体的な質問であれば回答の具体的になるので、より具体的に質問の記載する</li>
                            <li>TIC Chatbotを使用しも不具合の解決に至る答えが出ない場合はTICに相談する</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Registration Modal */}
            {showRegisterModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <button className="modal-close" onClick={handleCloseModal}>
                                ×
                            </button>
                            <div className="modal-header-main">
                                <img src="./memo.png" alt="memo-icon" className="modal-memo-icon" />
                                <div className="modal-title-section">
                                    <h2>
                                        {modalLanguage === 'Japanese' ? 'アクセス権限要求' : 'Access Permission Request'}
                                    </h2>
                                    <p className="modal-subtitle">
                                        {modalLanguage === 'Japanese'
                                            ? '会社/氏名/UIDをお知らせください'
                                            : 'Enter company name, username, and UID'}
                                    </p>
                                </div>
                                <LanguageDropDown setLanguage={setModalLanguage} translate={translate} />
                            </div>
                        </div>
                        <form onSubmit={handleSubmitRegistration} className="registration-form">
                            {/* Name Field */}
                            <div className="form-group">
                                <label htmlFor="name">
                                    {modalLanguage === 'Japanese' ? '氏名' : 'Name'}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    placeholder={modalLanguage === 'Japanese' ? '山田 太郎' : 'Taro Yamada'}
                                />
                            </div>

                            {/* Email Field */}
                            <div className="form-group">
                                <label htmlFor="email">
                                    {modalLanguage === 'Japanese' ? 'メールID' : 'Email ID'}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    placeholder={modalLanguage === 'Japanese' ? 'taro.yamada@company.co.jp' : 'taro.yamada@company.co.jp'}
                                />
                            </div>

                            {/* User ID Field */}
                            <div className="form-group">
                                <label htmlFor="userId">
                                    {modalLanguage === 'Japanese' ? 'ユーザーID' : 'User ID'}
                                </label>
                                <input
                                    type="text"
                                    id="userId"
                                    name="userId"
                                    required
                                    placeholder="Q1A2B3C"
                                />
                            </div>

                            {/* Company Name Field */}
                            <div className="form-group">
                                <label htmlFor="companyName">
                                    {modalLanguage === 'Japanese' ? '会社名' : 'Company Name'}
                                </label>
                                <input
                                    type="text"
                                    id="companyName"
                                    name="companyName"
                                    required
                                    placeholder={modalLanguage === 'Japanese' ? '株式会社例' : 'ABC Corporation'}
                                />
                            </div>

                            {/* Branch Field */}
                            <div className="form-group">
                                <label htmlFor="branch">
                                    {modalLanguage === 'Japanese' ? '支店' : 'Branch'}
                                </label>
                                <input
                                    type="text"
                                    id="branch"
                                    name="branch"
                                    required
                                    placeholder={modalLanguage === 'Japanese' ? '東京支店' : 'Tokyo Branch'}
                                />
                            </div>

                            {/* Department/Role Field */}
                            <div className="form-group">
                                <label htmlFor="department">
                                    {modalLanguage === 'Japanese' ? '部門/役割' : 'Department/Role'}
                                </label>
                                <input
                                    type="text"
                                    id="department"
                                    name="department"
                                    required
                                    placeholder={modalLanguage === 'Japanese' ? '営業部' : 'Sales Department'}
                                />
                            </div>



                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={handleCloseModal} disabled={isSubmitting}>
                                    {modalLanguage === 'Japanese' ? 'キャンセル' : 'Cancel'}
                                </button>
                                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <Spinner size="sm" mr={2} />
                                            {modalLanguage === 'Japanese' ? '送信中...' : 'Submitting...'}
                                        </>
                                    ) : (
                                        modalLanguage === 'Japanese' ? '申請を送信' : 'Submit Application'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const LoginPage = () => {
    return (
        <ChakraProvider resetCSS={false}>
            <LoginPageContent />
        </ChakraProvider>
    );
};

export default LoginPage;
